import * as React from "react";

import type { DialogProps } from "@radix-ui/react-dialog";

import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";

import { cn } from "../../utils";
import { Dialog, DialogContent } from "./dialog";

const Command = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) => (
  <CommandPrimitive
    className={cn(
      "af-flex af-h-full af-w-full af-flex-col af-overflow-hidden af-rounded-md af-border af-border-border af-bg-popover af-text-popover-foreground af-shadow-md",
      className,
    )}
    {...props}
  />
);
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({ children, ...props }: DialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="af-overflow-hidden af-p-0 af-shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:af-px-2 [&_[cmdk-group-heading]]:af-font-medium [&_[cmdk-group-heading]]:af-text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:af-pt-0 [&_[cmdk-group]]:af-px-2 [&_[cmdk-input-wrapper]_svg]:af-h-5 [&_[cmdk-input-wrapper]_svg]:af-w-5 [&_[cmdk-input]]:af-h-12 [&_[cmdk-item]]:af-px-2 [&_[cmdk-item]]:af-py-3 [&_[cmdk-item]_svg]:af-h-5 [&_[cmdk-item]_svg]:af-w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
};

const CommandInput = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) => (
  <div
    className="af-flex af-items-center af-border-b af-border-border af-px-3"
    cmdk-input-wrapper=""
  >
    <Search className="af-mr-2 af-h-4 af-w-4 af-shrink-0 af-opacity-50" />
    <CommandPrimitive.Input
      className={cn(
        "af-flex af-h-11 af-w-full af-rounded-md af-bg-transparent af-py-3 af-text-sm af-outline-none placeholder:af-text-muted-foreground disabled:af-cursor-not-allowed disabled:af-opacity-50",
        className,
      )}
      {...props}
    />
  </div>
);

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) => (
  <CommandPrimitive.List
    className={cn(
      "af-max-h-[300px] af-overflow-y-auto af-overflow-x-hidden",
      className,
    )}
    {...props}
  />
);

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = ({
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) => (
  <CommandPrimitive.Empty
    className="af-py-6 af-text-center af-text-sm"
    {...props}
  />
);

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = ({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) => (
  <CommandPrimitive.Group
    className={cn(
      "af-overflow-hidden af-p-1 af-text-foreground [&_[cmdk-group-heading]]:af-px-2 [&_[cmdk-group-heading]]:af-py-1.5 [&_[cmdk-group-heading]]:af-text-xs [&_[cmdk-group-heading]]:af-font-medium [&_[cmdk-group-heading]]:af-text-muted-foreground",
      className,
    )}
    {...props}
  />
);

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn("af--mx-1 af-h-px af-bg-border", className)}
    {...props}
  />
);
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      "af-relative af-flex af-cursor-default af-select-none af-items-center af-gap-2 af-rounded-sm af-px-2 af-py-1.5 af-text-sm af-outline-none data-[disabled=true]:af-pointer-events-none data-[selected=true]:af-bg-accent data-[selected=true]:af-text-accent-foreground data-[disabled=true]:af-opacity-50 [&_svg]:af-pointer-events-none [&_svg]:af-size-4 [&_svg]:af-shrink-0",
      className,
    )}
    {...props}
  />
);

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        "af-ml-auto af-text-xs af-tracking-widest af-text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
};
CommandShortcut.displayName = "CommandShortcut";

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
